<template>
  <div class="share-overlay" @click.self="close">
    <div class="share-shell">
      <button type="button" class="share-close" @click="close">×</button>

      <div class="share-formats">
        <button
          v-for="fmt in formats"
          :key="fmt.key"
          type="button"
          class="share-formats__option"
          :class="{
            'share-formats__option--active': selectedFormat === fmt.key,
          }"
          @click="selectFormat(fmt.key)"
        >
          {{ fmt.label }}
        </button>
      </div>

      <div class="share-stage">
        <img
          v-if="previewUrl"
          class="share-preview"
          :class="{ 'share-preview--tall': selectedFormat !== '16:9' }"
          :src="previewUrl"
          alt="Bet slip share card"
        />
        <div v-else-if="renderFailed" class="share-fallback">
          Couldn't render the image — you can still share the text below.
        </div>
        <div v-else class="share-fallback">Rendering…</div>
      </div>

      <div class="share-actions">
        <button
          v-if="canShareImage"
          type="button"
          class="share-actions__primary"
          @click="shareImage"
        >
          Share Image
        </button>
        <button
          type="button"
          :class="
            canShareImage
              ? 'share-actions__secondary'
              : 'share-actions__primary'
          "
          @click="postToX"
        >
          Post on 𝕏
        </button>
        <button
          v-if="canCopyImage"
          type="button"
          class="share-actions__secondary"
          @click="copyImage"
        >
          {{ copiedImage ? 'Copied!' : 'Copy Image' }}
        </button>
        <button
          type="button"
          class="share-actions__secondary"
          @click="downloadImage"
        >
          Download PNG
        </button>
      </div>
      <p v-if="xHint" class="share-hint">
        Image copied — paste it into your post.
      </p>
    </div>
  </div>
</template>

<script setup>
//CORE
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import download from 'downloadjs';
import { buildShareSlipMessage } from '@/core/shareSlipUtils';
import {
  renderShareCardBlob,
  SHARE_CARD_FORMATS,
} from '@/core/shareCardRenderer';
import PostHogUtils from '@/core/PostHogUtils';

//PROPS
const props = defineProps({
  slip: {
    type: Object,
    required: true,
  },
});

//EMITTERS
const emit = defineEmits(['closeModal']);

//CONST
const formats = Object.entries(SHARE_CARD_FORMATS).map(([key, spec]) => ({
  key,
  label: spec.label,
}));
// The feed crop takes far more space on Instagram, so mobile users (the ones
// posting from the IG app) start there; desktop starts on the X card.
const isMobileViewport = window.matchMedia?.('(max-width: 768px)')?.matches;
const selectedFormat = ref(isMobileViewport ? '4:5' : '16:9');
const previewUrl = ref('');
const renderFailed = ref(false);
const copiedImage = ref(false);
const xHint = ref(false);
// Per-format blob + object-URL cache so switching is instant.
const blobCache = {};
const urlCache = {};

//COMPUTED
const shareUrl = computed(() => 'https://4casters.io');
const shareMessage = computed(() =>
  buildShareSlipMessage(props.slip, shareUrl.value),
);
const canCopyImage = computed(
  () =>
    typeof window.ClipboardItem === 'function' &&
    Boolean(navigator?.clipboard?.write),
);
const canShareImage = computed(() => typeof navigator?.share === 'function');

//HOOK
onMounted(() => {
  PostHogUtils.capture('bet_share_opened', {
    outcome: props.slip?.outcome || 'pending',
    league: props.slip?.league,
    format: selectedFormat.value,
  });
  renderFormat(selectedFormat.value);
});

onBeforeUnmount(() => {
  Object.values(urlCache).forEach((url) => URL.revokeObjectURL(url));
});

//FUNCTIONS
async function renderFormat(format) {
  renderFailed.value = false;
  if (!blobCache[format]) {
    previewUrl.value = '';
    try {
      blobCache[format] = await renderShareCardBlob(props.slip, format);
      urlCache[format] = URL.createObjectURL(blobCache[format]);
    } catch {
      if (format === selectedFormat.value) renderFailed.value = true;
      return;
    }
  }
  if (format === selectedFormat.value) previewUrl.value = urlCache[format];
}

function selectFormat(format) {
  if (selectedFormat.value === format) return;
  selectedFormat.value = format;
  renderFormat(format);
}

function close() {
  emit('closeModal');
}

function currentBlob() {
  return blobCache[selectedFormat.value] || null;
}

function slipFileName() {
  const suffix = selectedFormat.value.replace(':', 'x');
  return `4casters-slip-${props.slip?.id || Date.now()}-${suffix}.png`;
}

function captureExport(method) {
  PostHogUtils.capture('bet_share_exported', {
    method,
    outcome: props.slip?.outcome || 'pending',
    format: selectedFormat.value,
  });
}

function downloadImage() {
  const blob = currentBlob();
  if (!blob) return;
  download(blob, slipFileName(), 'image/png');
  captureExport('download');
}

async function copyImage() {
  const blob = currentBlob();
  if (!blob || !canCopyImage.value) return false;
  try {
    // Safari requires the ClipboardItem value to be a Promise resolving to the
    // blob; Chrome accepts either. Passing a promise satisfies both.
    await navigator.clipboard.write([
      new window.ClipboardItem({ 'image/png': Promise.resolve(blob) }),
    ]);
    copiedImage.value = true;
    setTimeout(() => {
      copiedImage.value = false;
    }, 1800);
    captureExport('copy_image');
    return true;
  } catch {
    return false;
  }
}

async function shareImage() {
  const blob = currentBlob();
  try {
    if (blob && typeof navigator?.canShare === 'function') {
      const file = new File([blob], slipFileName(), {
        type: 'image/png',
      });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: '4Casters Bet Slip',
          text: shareMessage.value,
        });
        captureExport('native_share_image');
        return;
      }
    }
    await navigator.share({
      title: '4Casters Bet Slip',
      text: shareMessage.value,
      url: shareUrl.value,
    });
    captureExport('native_share_text');
  } catch (error) {
    if (error?.name !== 'AbortError') {
      await copyImage();
    }
  }
}

function postToX() {
  // Open the intent synchronously: awaiting the clipboard write first would
  // consume the user-activation gesture and get the popup blocked. The copy
  // runs after, so the image is on the clipboard by the time the composer is
  // ready to paste into.
  const text = encodeURIComponent(shareMessage.value);
  window.open(
    `https://twitter.com/intent/tweet?text=${text}`,
    '_blank',
    'noopener',
  );
  copyImage().then((copied) => {
    xHint.value = copied;
  });
  captureExport('x_intent');
}
</script>

<style scoped lang="scss">
.share-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 22, 51, 0.72);
  padding: 1.25rem;
}

.share-shell {
  position: relative;
  width: min(560px, 100%);
}

.share-close {
  position: absolute;
  top: -2.4rem;
  right: -0.1rem;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
}

.share-formats {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
}

.share-formats__option {
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  background: transparent;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.4rem 0.9rem;
  cursor: pointer;

  &--active {
    background: #fff;
    color: #343b86;
    border-color: #fff;
  }
}

.share-stage {
  border-radius: 18px;
  padding: 0.7rem;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: center;
}

.share-preview {
  display: block;
  max-width: 100%;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);

  &--tall {
    max-height: min(58vh, 560px);
    width: auto;
  }
}

.share-fallback {
  padding: 3rem 1rem;
  text-align: center;
  color: #e8ebff;
  font-size: 0.9rem;
}

.share-actions {
  margin-top: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.share-actions__primary,
.share-actions__secondary {
  border-radius: 999px;
  border: 0;
  padding: 0.75rem 0.9rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
}

.share-actions__primary {
  background: #343b86;
  color: #fff;
}

.share-actions__secondary {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
}

.share-hint {
  margin: 0.7rem 0 0;
  text-align: center;
  color: #d5daf5;
  font-size: 0.78rem;
}
</style>
